/** The palette's entries: the static groups (queues, views, actions, Labs), the live ones (people, events), and the filter that ranks them. */
import { canSee, JOBS } from "../app/jobs";
import { shortDate } from "../data/format";
import { peopleHref, type QueueRow } from "../data/queues";
import { eventRefIn } from "../app/crumbs";
import { eventHref, type EventRow } from "../data/events";
import { PANELS } from "../screens/email/panelKeys";
import { GROUPS as EVENT_GROUPS, groupIn } from "../screens/events/model";
import { DEFAULT_VIEW, DISTRICTS, districtViewId, resolveView } from "../screens/people/views";
import { LABS } from "../screens/labs/entries";

export type GroupId = "needs" | "recent" | "people" | "actions" | "views" | "labs" | "search-failed";

/** Needs you and Recent are the resting list; People, Actions, Views and Labs answer a query. Actions come before Views so an action's own name ("Feedback", "Export CSV") takes Enter before a sheet that merely mentions it. */
export const GROUPS: readonly { id: GroupId; label: string }[] = [
  { id: "needs", label: "Needs you" },
  { id: "recent", label: "Recent" },
  { id: "people", label: "People" },
  { id: "actions", label: "Actions" },
  { id: "views", label: "Views" },
  { id: "labs", label: "Labs" },
  // Last, so a failed search never takes Enter from a match that did load.
  { id: "search-failed", label: "People" },
];

export type ActionId = "copy-emails" | "export-csv" | "feedback" | "help";

export type Target =
  | { kind: "person"; id: number }
  | { kind: "view"; id: string }
  | { kind: "all-views" }
  | { kind: "lab"; id: string }
  | { kind: "route"; to: string }
  | { kind: "action"; id: ActionId }
  | { kind: "retry-search" };

export interface Entry {
  key: string;
  group: GroupId;
  label: string;
  /** Muted text after the label (a person's sublabel). */
  hint?: string;
  /** Extra words that match but are not shown. */
  keywords?: string;
  /** Literal match offset in `label` (people only). */
  match?: { start: number; length: number };
  target: Target;
}

/** The People view ids with the titles People shows (entries.test pins them to the registry) and the words that also find them. */
const VIEWS: readonly [id: string, title: string, keywords: string][] = [
  ["all", "Everyone", "all members people"],
  ["active", "Active members", "current paid"],
  ["lapsed", "Lapsed", "window grace"],
  ["lapsed-65", "Lapsed within 65 days", "lapsed window"],
  ["lapsed-120", "Lapsed 66–120 days", "lapsed window"],
  ["suspended", "Suspended", ""],
  ["at-risk", "At risk", "renewal"],
  ["payment-issues", "Payment problems", "failed due dues"],
  ["licence-changes", "Licence changes", "state register"],
  ["arrivals", "Joined and rejoined", "arrivals new"],
  ["licences", "Licences", "licenses license renew expiry state list within 90 days expired"],
  ["state-emails", "State emails", "placeholder made up chiromi email state list licence"],
  ["ce21-fixes", "Fix in CE21", "flagged licence number state list correct ce21"],
  ["held", "Held for review", "held review"],
  ["opted-out", "Opted out", "unsubscribed"],
  ["not-in-ac", "Not in ActiveCampaign", "email missing"],
  ["win-back", "Win-back", "lapsed rejoin"],
  ["recently-added-ac", "Recently added to ActiveCampaign", "new contacts batch"],
];

export const VIEW_ENTRIES: readonly Entry[] = VIEWS.map(([id, title, keywords]) => ({
  key: `view:${id}`,
  group: "views",
  label: title,
  keywords,
  target: { kind: "view", id },
}));

export const DISTRICT_ENTRIES: readonly Entry[] = DISTRICTS.map((n) => ({
  key: `view:${districtViewId(n)}`,
  group: "views",
  label: `District ${n}`,
  keywords: "region",
  target: { kind: "view", id: districtViewId(n) },
}));

/** One row per event, its page: upcoming first, as the API gives them. */
export function eventEntries(events: readonly EventRow[] | undefined): Entry[] {
  return (events ?? []).map((event) => ({
    key: `event:${event.ref}`,
    group: "views",
    label: event.name,
    hint: event.starts_on ? shortDate(event.starts_on) : undefined,
    keywords: "event registered",
    target: { kind: "route", to: eventHref(event.ref) },
  }));
}

/** One row a queue, with the count the sidebar shows: each opens the People view that lists exactly those people. */
export function queueEntries(queues: readonly QueueRow[]): Entry[] {
  return queues.map((q) => ({ key: `queue:${q.key}`, group: "needs", label: q.reason, hint: q.count.toLocaleString("en-US"), target: { kind: "view", id: q.view } }));
}

/** At rest: the queues, the people opened lately, then one row that opens every view. */
export function restEntries(queues: readonly Entry[], recent: readonly Entry[]): Entry[] {
  return [...queues, ...recent, { key: "all-views", group: "views", label: "All views", target: { kind: "all-views" } }];
}

/** "labs" and "lab" find all nine: Labs is reached from here only. */
export const LAB_ENTRIES: readonly Entry[] = LABS.map((lab) => ({
  key: `lab:${lab.id}`,
  group: "labs",
  label: lab.name,
  hint: "Not live yet",
  keywords: "labs lab",
  target: { kind: "lab", id: lab.id },
}));

/** The reports behind People's Reports menu (`?report=`) and the Settings sheets (`?open=`): key, title, the words that also find them. The titles are pinned to the screens in the tests. */
const REPORTS: readonly [key: string, title: string, keywords: string][] = [
  ["growth", "Growth", "monthly trend"],
  ["renewals", "Retention and renewals", "retention renew"],
  ["composition", "Composition", "tiers mix"],
  ["scorecard", "Scorecard", "numbers"],
];
/** Money's two sheets (`?panel=`). */
const MONEY_SHEETS: readonly [key: string, title: string, keywords: string][] = [
  ["revenue", "Revenue", "dues income totals tiers finance"],
  ["reconciliation", "Reconciliation", "payments affinipay"],
];
export const SETTINGS_SHEETS: readonly [key: string, title: string, keywords: string][] = [
  ["users", "Users", "accounts roles access"],
  ["audit", "Audit", "log history changes"],
  ["reports", "My reports", "feedback issues"],
  ["sync", "Data and sync", "activecampaign push mirror reconciliation drift"],
  ["activity", "Sync activity", "activecampaign push log waves runs"],
  ["quality", "Data quality", "errors duplicates problems"],
  ["exports", "Exports", "export download files"],
  ["imports", "Imports", "state licence list addresses accounts report loads history undo decide"],
];

interface PageOptions {
  pathname: string;
  search: string;
  capabilities: readonly string[] | undefined;
}

/** Every report and sheet by its name, each opening by its URL. A People report keeps the view on screen. */
export function pageEntries({ pathname, search, capabilities }: PageOptions): Entry[] {
  const view = (pathname.startsWith("/people") && new URLSearchParams(search).get("view")) || DEFAULT_VIEW;
  const page = (to: string, label: string, place: string, keywords = ""): Entry => ({ key: `page:${to}`, group: "views", label, hint: place, keywords, target: { kind: "route", to } });
  const money = JOBS.find((job) => job.to === "/money");
  const email = JOBS.find((job) => job.to === "/email");
  return [
    ...REPORTS.map(([key, title, keywords]) => page(`/people?view=${encodeURIComponent(view)}&report=${key}`, title, "People", keywords)),
    // Email's (#716): staff and the manager.
    ...(email && canSee(email, capabilities)
      ? [page("/email?campaign=new", "New campaign", "Email", "write draft send test activecampaign"), ...PANELS.map(({ key, label }) => page(`/email?panel=${key}`, label, "Email", "audience activecampaign"))]
      : []),
    ...(money && canSee(money, capabilities) ? MONEY_SHEETS.map(([key, title, keywords]) => page(`/money?panel=${key}`, title, "Money", keywords)) : []),
    // Imports is a page of its own (the hub), the rest are sheets.
    ...SETTINGS_SHEETS.map(([key, title, keywords]) => page(key === "imports" ? "/settings/imports" : `/settings?open=${key}`, title, "Settings", keywords)),
  ];
}

/** Where Copy emails and Export CSV act: a page with a list of people (People, an event's page). */
export const hasPeopleList = (pathname: string): boolean => pathname.startsWith("/people") || eventRefIn(pathname) !== null;

/** The list Copy emails and Export CSV act on: the People view on screen, an event's group on its page, else Everyone (they go there first). */
export function listScope(pathname: string, search: string, events: readonly EventRow[] | undefined): string {
  const ref = eventRefIn(pathname);
  if (ref) {
    const group = groupIn(new URLSearchParams(search).get("group"));
    const name = events?.find((e) => e.ref === ref)?.name ?? "Event";
    return group === "everyone" ? name : `${name}: ${EVENT_GROUPS.find((g) => g.value === group)?.label}`;
  }
  if (!pathname.startsWith("/people")) return "Everyone";
  return resolveView(new URLSearchParams(search).get("view") || DEFAULT_VIEW)?.title || "Everyone";
}

interface ActionOptions {
  /** The tenant role's capabilities; undefined while loading (gated jobs stay out until known). */
  capabilities: readonly string[] | undefined;
  /** The shell's dialogs: an action exists only when the shell gave the way to open it. */
  feedback: boolean;
  help: boolean;
  /** The list the two People actions work on (`listScope`), shown after them. */
  scope: string;
}

/** What can be done from here: act on a People list (from anywhere), go to a job (this one too: it closes a sheet), open a shell dialog. */
export function actionEntries({ capabilities, feedback, help, scope }: ActionOptions): Entry[] {
  const action = (id: ActionId, label: string, keywords: string, hint?: string): Entry => ({ key: `action:${id}`, group: "actions", label, hint, keywords, target: { kind: "action", id } });
  return [
    action("copy-emails", "Copy emails", "clipboard", scope),
    action("export-csv", "Export CSV", "download spreadsheet", scope),
    // Any file Keystone loads (the state licence list, the addresses), from anywhere: the Imports hub.
    { key: "action:import", group: "actions", label: "Import a file…", hint: "Settings", keywords: "load upload state licence license list addresses accounts report xlsx csv", target: { kind: "route", to: "/settings/imports" } },
    ...JOBS.filter((job) => job.to !== "/people" && canSee(job, capabilities)).map(
      (job): Entry => ({ key: `go:${job.to}`, group: "actions", label: `Go to ${job.label}`, keywords: "open", target: { kind: "route", to: job.to } }),
    ),
    ...(feedback ? [action("feedback", "Feedback", "report problem idea")] : []),
    ...(help ? [action("help", "Keyboard shortcuts", "help keys")] : []),
  ];
}

/** 0 = label starts with the query, 1 = a word does, 2 = label contains it, 3 = a keyword starts with it, -1 = no match. */
export function matchRank(entry: Pick<Entry, "label" | "keywords">, query: string): number {
  const q = query.trim().toLowerCase();
  if (!q) return 0;
  const label = entry.label.toLowerCase();
  if (label.startsWith(q)) return 0;
  if (label.split(/\s+/).some((word) => word.startsWith(q))) return 1;
  if (label.includes(q)) return 2;
  if (entry.keywords?.toLowerCase().split(/\s+/).some((word) => word.startsWith(q))) return 3;
  return -1;
}

/** Entries that match, best first; equal ranks keep their given order. An empty query keeps everything. */
export function filterEntries<T extends Pick<Entry, "label" | "keywords">>(entries: readonly T[], query: string): T[] {
  return entries
    .map((entry, order) => ({ entry, order, rank: matchRank(entry, query) }))
    .filter((row) => row.rank >= 0)
    .sort((a, b) => a.rank - b.rank || a.order - b.order)
    .map((row) => row.entry);
}

/** Group the entries in the fixed group order, dropping empty groups. */
export function grouped(entries: readonly Entry[]): { id: GroupId; label: string; items: Entry[] }[] {
  return GROUPS.map((g) => ({ ...g, items: entries.filter((e) => e.group === g.id) })).filter((g) => g.items.length > 0);
}

/** Where choosing an entry goes, or null for a person (who opens where you are: `personRoute`), an action or an in-place row. */
export function routeFor(target: Target): string | null {
  switch (target.kind) {
    case "view":
      return peopleHref(target.id);
    case "lab":
      return `/labs/${target.id}`;
    case "route":
      return target.to;
    default:
      return null;
  }
}

const OTHER_SHEETS = ["open", "run", "panel", "order", "report", "campaign"];

/** A person opens in the sheet on the page you are on, over a list that holds them. On People, a view that does not list them gives way to Everyone; another sheet's parameter gives way to the person's. */
export function personRoute(id: number, { pathname, search }: { pathname: string; search: string }, listed: boolean): string {
  const params = new URLSearchParams(search);
  OTHER_SHEETS.forEach((name) => params.delete(name));
  params.set("person", String(id));
  if (pathname.startsWith("/people") && !listed) params.set("view", DEFAULT_VIEW);
  return `${pathname}?${params}`;
}
